/**
 * Krok UXR3 — KARTA „DALŠÍ KROK" (zadani bod 5, predloha `Zakazka.dc.html`).
 *
 * Nalez R.B. 2. 10. 2026 23:34: tlacitko „Další krok" bylo urizlute a nekdy
 * neslo o krok, ale o SUROVOU OTAZKU AUDITU („Odpovězte na otázku: Jaká je
 * indikativní cena za celek 2807 m² …"). FIX6 bod 5 to zmirnil zkracenim
 * popisku; UXR3 to resi poradne: dalsi krok je KARTA s lidskou vetou a
 * NEJVYS DVEMA tlacitky, a vzdy stoji na tom, co o zakazce vime.
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTI UXR3-KROK — co je „dalsi krok"
 *
 * Poradi pravidel, prvni ktere plati, vyhrava:
 *
 *  1. **Proverení neni hotove** → „Dokonči prověření". Veta rekne, kolik
 *     oblasti chybi a kolik z nich si REOS dotahne sam; prvni tlacitko je
 *     „Dohledat automaticky (N)", kdyz N > 0, druhe „Zadat ručně".
 *  2. **Proverení hotove, report klient jeste nema** → „Pošli klientovi
 *     report" s odkazem na Dokumenty (karta reportu z UXR2-4).
 *  2b. **Proverení hotove, inzerat jeste neodesel** (krok UXV4b, zakazka na
 *     prodej/pronajem) → „Připrav inzerát" s tlacitkem „Připravit inzerát"
 *     (`/zakazky/$id/inzerat`). Stav inzeratu nese `listingState`; `null`
 *     = nevime / zakazka inzerat nema — pak se pravidlo preskoci.
 *  3. **Neco jineho je otevrene** → prvni otevreny ukol kokpitu, a to jeho
 *     KRATKYM popiskem (`shortText`, FIX6 bod 5), nikdy celou otazkou.
 *     Cela veta ukolu je v tele karty, kde je na ni misto.
 *  4. **Nic** → karta rekne, ze nic neceka, a nema zadne tlacitko. Vymyslet
 *     akci do prazdna znamena poslat maklere nekam, kde nic nezbyva
 *     (tentyz duvod jako ROZHODNUTI UXR2-5).
 *
 * Vypocet je ciste funkce nad daty, ktera stranka uz ma — zadny novy dotaz.
 * ---------------------------------------------------------------------------
 */

import { t } from '@reos/i18n';
import type { CockpitTask } from './case-cockpit.js';
import type { CaseProgressView } from './case-progress.js';
import type { CaseReviewAreas } from './case-review-areas.js';
import type { CaseSubject } from './case-subject.js';

const cs = { locale: 'cs' } as const;

/** Co tlacitko karty udela. Vykresleni (a navigaci) resi obrazovka. */
export type CaseAdviceAction =
  /** Spustit dohledani z verejnych zdroju (placeny beh, s potvrzenim). */
  | { readonly kind: 'lookup'; readonly label: string; readonly count: number }
  /** Posunout na seznam oblasti proverení na teze obrazovce. */
  | { readonly kind: 'areas'; readonly label: string }
  /** Otevrit jinou sekci detailu. */
  | { readonly kind: 'section'; readonly label: string; readonly section: string }
  /** Spustit akci prvniho otevreneho ukolu kokpitu. */
  | { readonly kind: 'task'; readonly label: string; readonly taskId: string }
  /** Krok UXV4b: otevrit „Nový inzerát" zakazky. */
  | { readonly kind: 'listing'; readonly label: string };

export interface CaseAdvice {
  /** Nadpis karty — lidsky pokyn, ne otazka. */
  readonly title: string;
  /** Jeden az dva odstavce textu, proc a co presne. */
  readonly body: string;
  /** Nejvys dve tlacitka (zadani UXR3 bod 5). */
  readonly actions: readonly CaseAdviceAction[];
}

export interface CaseAdviceInput {
  /** Krok UXV4a: tentyz pohled na postup, jaky kresli prouzek (jedna funkce). */
  readonly progress: CaseProgressView;
  readonly review: CaseReviewAreas;
  readonly subject: CaseSubject;
  readonly tasks: readonly CockpitTask[];
  /** Ma klient uz verzi reportu? `null` = nevime (volani selhalo). */
  readonly clientReportSent: boolean | null;
  /**
   * Krok UXV4b: stav inzeratu zakazky (`GET …/listing-draft/summary`).
   * `null` = nevime nebo zakazka inzerat nema (neni na prodej/pronajem).
   */
  readonly listingState?: 'none' | 'draft' | 'submitted' | null | undefined;
}

export function caseAdvice(input: CaseAdviceInput): CaseAdvice {
  const { progress, review, subject, tasks } = input;
  const missing = review.total - review.verified;
  const firstOpen = tasks.find((task) => !task.done) ?? null;

  // Zrusena zakazka zadny dalsi krok nema — rikat ho by bylo matouci.
  if (progress.cancelled) {
    return {
      title: t('webAdmin.caseAdvice.cancelledTitle', cs),
      body: t('webAdmin.caseAdvice.cancelledBody', cs),
      actions: [],
    };
  }

  // 1. Proverení neni hotove.
  if (missing > 0) {
    const lookupCount = review.selfFetchable;
    const actions: CaseAdviceAction[] = [];
    if (lookupCount > 0) {
      actions.push({
        kind: 'lookup',
        label: t('webAdmin.caseAdvice.lookupAction', { ...cs, values: { count: String(lookupCount) } }),
        count: lookupCount,
      });
    }
    /*
     * Krok UXR5 bod 10: druhe tlacitko je VZDY „Zadat ručně" (predloha
     * `Zakazka.dc.html`). Do UXR5 se bez dohledatelne oblasti prepnulo na
     * „Ukázat, co chybí" — na snimku UXR3 to bylo JEDINE tlacitko karty a
     * neslibovalo akci, jen posun o kus niz.
     */
    actions.push({ kind: 'areas', label: t('webAdmin.caseAdvice.enterAction', cs) });
    return {
      title: t('webAdmin.caseAdvice.reviewTitle', { ...cs, values: { subject: subject.label } }),
      body:
        lookupCount > 0
          ? t('webAdmin.caseAdvice.reviewBodyMixed', {
              ...cs,
              values: { missing: String(missing), lookup: String(lookupCount) },
            })
          : t('webAdmin.caseAdvice.reviewBodyManual', { ...cs, values: { missing: String(missing) } }),
      actions,
    };
  }

  // 2. Proverení hotove, report klient jeste nema.
  if (input.clientReportSent === false) {
    return {
      title: t('webAdmin.caseAdvice.reportTitle', cs),
      body: t('webAdmin.caseAdvice.reportBody', cs),
      actions: [
        {
          kind: 'section',
          label: t('webAdmin.caseAdvice.reportAction', cs),
          section: 'dokumenty',
        },
      ],
    };
  }

  // 2b. Proverení hotove, inzerat jeste neodesel (krok UXV4b).
  if (input.listingState === 'none' || input.listingState === 'draft') {
    return {
      title: t('webAdmin.listingDraft.nextStep.title', cs),
      body: t('webAdmin.listingDraft.nextStep.body', cs),
      actions: [{ kind: 'listing', label: t('webAdmin.listingDraft.nextStep.action', cs) }],
    };
  }

  // 3. Prvni otevreny ukol — kratkym popiskem, cela veta v tele karty.
  if (firstOpen !== null) {
    return {
      title: firstOpen.shortText,
      body: firstOpen.text,
      actions: [
        {
          kind: 'task',
          label: t('webAdmin.caseAdvice.taskAction', cs),
          taskId: firstOpen.id,
        },
      ],
    };
  }

  // 4. Nic neceka.
  return {
    title: t('webAdmin.caseAdvice.idleTitle', cs),
    body: t('webAdmin.caseAdvice.idleBody', cs),
    actions: [],
  };
}
